import { Alert, Skeleton, Stack } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import type { FactoryAssetPlatform } from '@/lib/constants/appFactory';
import { useFactoryAssetSets } from '../useAppFactory';
import { AppCurrentImages } from './AppCurrentImages';
import { AppBrandKit } from './AppBrandKit';
import { AppManualSet } from './AppManualSet';

interface AppImagesPanelProps {
  appCfgId: number;
  /** Whose deliverable set this panel manages (roku, firetv). */
  platform: FactoryAssetPlatform;
  disabled?: boolean;
}

/**
 * The Images surface orchestrator — platform-generic since 3.34.0 (the Fire TV
 * tab mounts it for the set an Android build is made with; Roku keeps its own).
 * Every card on this panel reads the factory gateway, so an unreachable gateway
 * must say so — three cards each quietly returning null would leave the whole
 * panel blank (the pre-rework tab always had non-factory content to fall back
 * on; this one does not).
 */
export function AppImagesPanel({ appCfgId, platform, disabled }: AppImagesPanelProps) {
  const assetSetsQuery = useFactoryAssetSets(appCfgId, platform);

  if (assetSetsQuery.isError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="orange">
        Image management is temporarily unavailable. Your app and its current images are
        unaffected — try again in a few minutes, or contact support if this persists.
      </Alert>
    );
  }
  if (assetSetsQuery.isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={120} radius="md" />
        <Skeleton height={220} radius="md" />
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <AppCurrentImages appCfgId={appCfgId} platform={platform} />
      <AppBrandKit appCfgId={appCfgId} platform={platform} disabled={disabled} />
      <AppManualSet appCfgId={appCfgId} platform={platform} disabled={disabled} />
    </Stack>
  );
}
